import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useFormikContext } from 'formik'
import { getMyTeamMembers } from '../../../../services/teamExperienceService'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

type Member = {
  id: number
  name: string
  image?: string
  imageUrl?: string
  position?: string
}

type Props = {
  values: number[]
  onChange: (val: number[]) => void
  placeholder?: string
  minChars?: number
  name?: string
}

const LIMIT = 20

export function PartnersAutocompleteField({
  values = [],
  onChange,
  placeholder = 'Select partners',
  minChars = 0,
  name,
}: Props) {

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [items, setItems] = useState<Member[]>([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedCache, setSelectedCache] = useState<Record<number, Member>>({})
  const formik = useFormikContext()

  const reqRef = useRef(0)
  const hasMore = items.length < count

  // 🔹 Debounce
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(id)
  }, [query])

  // 🔹 Fetch: offset 0 reemplaza, resto acumula. Cancela respuestas obsoletas.
  const fetchData = useCallback(async (search: string, nextOffset: number) => {
    const reqId = ++reqRef.current
    setLoading(true)
    try {
      const res = await getMyTeamMembers(search, LIMIT, nextOffset)
      if (reqId !== reqRef.current) return
      const results: Member[] = res?.results ?? []
      setCount(res?.count ?? 0)
      setItems((prev) => (nextOffset === 0 ? results : [...prev, ...results]))
    } catch {
      if (reqId === reqRef.current && nextOffset === 0) {
        setItems([])
        setCount(0)
      }
    } finally {
      if (reqId === reqRef.current) setLoading(false)
    }
  }, [])

  // 🔹 Nueva búsqueda: reinicia desde el inicio
  useEffect(() => {
    if (debounced.length < minChars) return
    setOffset(0)
    fetchData(debounced.toLowerCase(), 0)
  }, [debounced, minChars, fetchData])

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return
    const next = offset + LIMIT
    setOffset(next)
    fetchData(debounced.toLowerCase(), next)
  }, [loading, hasMore, offset, debounced, fetchData])

  const handleScroll = useNearBottom(loadMore)

  // 🔹 Recuerda los seleccionados aunque la búsqueda los deje fuera
  useEffect(() => {
    setSelectedCache((prev) => {
      const nextCache = { ...prev }
      for (const it of items) if (values.includes(it.id)) nextCache[it.id] = it
      return nextCache
    })
  }, [items, values])

  const handleAdd = (itemId: number) => {
    if (!values.includes(itemId)) {
      onChange([...values, itemId])
    }
    setQuery('')
    setOpen(false)
  }

  const handleRemove = (itemId: number) => {
    onChange(values.filter((v) => v !== itemId))
  }

  const validateForm = () => {
    if (name) {
      formik.setFieldTouched(name, true)
      formik.validateForm()
    }
  }

  const selectedObjects = values
    .map((id) => selectedCache[id])
    .filter(Boolean) as Member[]

  const visible = items

  return (
    <div className='nb-language-autocomplete'>
      <div className='nb-language-wrapper'>

        {selectedObjects.map((item) => (
          <div
            key={item.id}
            className="nb-admin-team-item-select"
          >
            <div className='tap-add-experience-2-image'>
              <img
                src={item.image ?? item.imageUrl}
                width={24}
                height={24}
                alt="avatar"
              />
            </div>

            <span>{item.name}</span>

            <div onClick={() => handleRemove(item.id)}>
              <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
            </div>
          </div>
        ))}

        <input disabled={true}
          className='tap-add-experience-input-category nb-language-input'
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            validateForm()
          }}
          onFocus={() => {
            setOpen(true)
            validateForm()
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
      </div>

      {open && (
        <div className='nb-language-menu' onScroll={handleScroll}>
          {visible.length === 0 && !loading && (
            <div className="empty-state">No results</div>
          )}

          {visible.map((item) => (
            <div
              className="tap-add-experience-2-invitee-card cursor-pointer"
              key={item.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAdd(item.id)}
            >
              <img
                className="tap-add-experience-2-invitee-card__avatar"
                src={item.image ?? item.imageUrl}
                alt="avatar"
              />

              <div className="tap-add-experience-2-invitee-card__name">
                {item.name}
              </div>

              <div className="tap-add-experience-2-invitee-card__role-wrapper">
                <div className="tap-add-experience-2-invitee-card__role">
                  {item.position}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="tap-add-experience-2-invitee-card" style={{ color: '#808080' }}>
              Loading…
            </div>
          )}
        </div>
      )}
    </div>
  )
}